import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { ElementType, ReactNode } from 'react';
import {
  ArrowRight,
  BarChart3,
  Brain,
  BriefcaseBusiness,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  Compass,
  FileText,
  Layers3,
  Monitor,
  Sparkles,
  Target,
  Users,
  X,
} from 'lucide-react';

export interface PresentationDirecteurTIModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'vision' | 'rencontre1' | 'cycle' | 'portail' | 'questions' | 'rencontres';

const tabs: { id: TabId; label: string; icon: ElementType }[] = [
  { id: 'vision', label: 'Vision', icon: Sparkles },
  { id: 'rencontre1', label: 'Rencontre 1', icon: Users },
  { id: 'cycle', label: 'Démarche', icon: CalendarClock },
  { id: 'portail', label: 'Portail', icon: Monitor },
  { id: 'questions', label: 'Questions', icon: Target },
  { id: 'rencontres', label: 'Rencontres 2-5', icon: ClipboardList },
];

const directorValue = [
  {
    title: 'Un seul point de contact',
    body: 'PdC principal chez BZ pour garder le partenariat clair, suivi et imputable.',
  },
  {
    title: 'Conseiller TI en solutions d’affaires',
    body: 'Conseil sans pression de vente, relié aux besoins d’affaires réels.',
  },
  {
    title: 'Acteur impartial du comité TI',
    body: 'Posture d’arbitrage et de valeur dans le comité TI existant ou à créer.',
  },
  {
    title: 'Stratégie 5 ans',
    body: 'Optimiser le budget TI selon la maturité, les priorités et les contraintes.',
  },
];

const meetingOneItems = [
  'Introduction à PRIsME',
  'Idéologie PRIsME : rigueur et efficacité',
  'Gouvernance, rôles et responsabilités du comité TI',
  'Cadre référentiel pour mesurer le progrès des piliers de la PME',
  'Tableau priorisé Besoins · Solutions · Coûts',
  'Portail PRIsME-Client',
  'Validation initiale de la gouvernance TI si le client poursuit l’approche PRIsME',
];

const partnershipSteps = [
  {
    title: 'Gouvernance',
    body: 'Encadrer le partenariat avec une matrice RACI décrivant les rôles et responsabilités des acteurs internes et externes du comité TI.',
  },
  {
    title: 'Cadre référentiel',
    body: 'Mesurer la maturité actuelle de l’entreprise, définir la maturité désirée et prioriser les activités qui font progresser les piliers choisis.',
  },
  {
    title: 'Au-delà de l’opérationnel',
    body: 'Ajouter et suivre rigoureusement des activités tactiques et stratégiques, pas seulement les demandes opérationnelles.',
  },
  {
    title: 'Preuve visuelle',
    body: 'Démontrer le progrès des activités, de la maturité, des indicateurs de performance et de la valeur créée par les solutions choisies.',
  },
];

const preCycleFacts = [
  'Approche rigoureuse centrée sur les besoins d’affaires prioritaires et les contraintes budgétaires / temporelles.',
  'Adéquation des solutions et interventions avec les besoins d’affaires, selon les priorités du client.',
  '2 à 3 rencontres de 2h maximum sur une fenêtre de 6 semaines.',
  '4 documents et un tableau produits pour le client.',
];

const deliverables = [
  'Tableau Besoins · Solutions · Coûts',
  'ODS',
  'Capture holistique et priorisée des besoins d’affaires',
  'Plan annuel TI',
  'Plan stratégique TI 5 ans',
];

const aiTransformation = [
  'Discussion et vulgarisation de l’avancement et de la portée des solutions en intelligence artificielle.',
  'Réflexion sur les opportunités de transformation numérique.',
  'Automatisation potentielle de tâches ou processus simples et répétitifs.',
  'Soutien aux employés et décideurs pour augmenter la performance et l’efficacité.',
  'Discussions préliminaires sur les subventions et crédits fédéraux, provinciaux et municipaux.',
];

const portalSections = [
  {
    title: 'Visualiser',
    items: ['Photo de maturité du cadre référentiel', 'Indicateurs de performance', 'Gouvernance', 'Suivis des réunions du comité TI'],
  },
  {
    title: 'Soutenir la gestion',
    items: ['Gestion documentaire', 'Rédaction semi-automatique possible', 'Projets et tâches stratégiques, tactiques et opérationnels', 'Catalogue des actifs TI', 'Solutions TI, humain et processus'],
  },
  {
    title: 'Insérer et accéder',
    items: ['Solutions spécifiques, comme la gestion de carrière', 'Activités de transformation numérique', 'Exploitation de l’IA'],
  },
  {
    title: 'Collaborer',
    items: ['Plateforme collaborative du comité TI', 'Version 2 en cours de construction'],
  },
];

const questions = [
  'Est-ce que vos services TI actuels répondent à vos attentes et vos besoins d’affaires?',
  'Est-ce qu’il y a des besoins ou objectifs d’affaires actuels/futurs qui requièrent une intervention?',
  'Avez-vous les budgets et le temps pour adresser ces nouveaux défis d’affaires?',
  'En tenant compte de vos besoins, de vos budgets et de vos contraintes, est-il opportun de poursuivre la démarche?',
];

const homework = [
  'Donner les disponibilités pour les prochaines rencontres.',
  'Réfléchir aux besoins d’affaires, à leurs priorités et aux contraintes de budget et de temps.',
  'Pour les nouveaux clients : identifier la personne qui peut aider à faire l’inventaire des actifs TI.',
];

const meetings = [
  {
    number: '#2',
    title: 'Discussion sur les besoins d’affaires',
    items: [
      'Validation de la gouvernance TI',
      'Validation du cadre et de ses piliers',
      'Évaluation de la maturité actuelle et définition de la maturité cible',
      'Présentation du catalogue des actifs à valider',
      'Prise de besoins holistiques, incluant les besoins d’affaires',
      'Priorisation des besoins holistiques sans contrainte de temps et d’argent',
    ],
    homework: 'Réfléchir aux contraintes de temps et de budget; prendre connaissance des ébauches de la rencontre 3.',
  },
  {
    number: '#3',
    title: 'Objectifs SMART et solutions compréhensives',
    items: [
      'Ébauche ou mise à jour du document des besoins holistiques',
      'Validation des besoins',
      'Ébauche du tableau Besoins · Solutions avec coûts associés',
      'Priorisation avec contraintes temporelles et budgétaires du client',
    ],
    homework: 'Réviser les besoins et contraintes; prendre connaissance des ébauches de la rencontre 4.',
  },
  {
    number: '#4',
    title: 'Révision des documents',
    items: [
      'Approbation du document final sur les besoins holistiques',
      'Présentation de l’ébauche du plan annuel',
      'Présentation de l’ébauche du plan stratégique 5 ans',
      'Approbation ou mise à jour de l’offre de service version finale',
    ],
    homework: 'Préparer l’approbation des documents finaux.',
  },
  {
    number: '#5',
    title: 'Approbation des documents finaux',
    items: [
      'Approbation du plan annuel version finale',
      'Approbation du plan stratégique version finale',
      'Approbation ou mise à jour de l’offre de service version finale',
    ],
    homework: 'Basculer vers l’exécution suivie dans la gouvernance TI.',
  },
];

function GlassCard({
  title,
  icon: Icon,
  children,
  tone = 'slate',
}: {
  title: string;
  icon: ElementType;
  children: ReactNode;
  tone?: 'slate' | 'cyan' | 'emerald' | 'violet' | 'amber';
}) {
  const styles = {
    slate: 'border-white/10 bg-white/[0.055]',
    cyan: 'border-cyan-400/20 bg-cyan-500/10',
    emerald: 'border-emerald-400/20 bg-emerald-500/10',
    violet: 'border-violet-400/20 bg-violet-500/10',
    amber: 'border-amber-400/20 bg-amber-500/10',
  }[tone];

  return (
    <div className={`rounded-3xl border p-5 ${styles}`}>
      <h3 className="mb-3 flex items-center gap-2 text-lg font-black text-white">
        <Icon className="h-5 w-5 text-cyan-200" />
        {title}
      </h3>
      <div className="text-sm leading-7 text-slate-200">{children}</div>
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="flex gap-2">
          <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-emerald-300" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function Metric({ value, label, detail }: { value: string; label: string; detail: string }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.055] p-4">
      <p className="text-3xl font-black text-white md:text-4xl">{value}</p>
      <p className="mt-2 text-sm font-bold uppercase tracking-wide text-cyan-200">{label}</p>
      <p className="mt-1 text-xs leading-5 text-slate-400">{detail}</p>
    </div>
  );
}

export default function PresentationDirecteurTIModal({ isOpen, onClose }: PresentationDirecteurTIModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('vision');

  const tabContent = useMemo(() => {
    if (activeTab === 'vision') {
      return (
        <div className="space-y-4">
          <div className="rounded-[2rem] border border-white/10 bg-linear-to-br from-white/10 via-cyan-500/10 to-emerald-500/10 p-5 md:p-6">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-cyan-200">Directeurs TI</p>
            <h3 className="max-w-5xl text-2xl font-black leading-tight text-white md:text-4xl">
              PRIsME est un partenariat TI novateur et mutuellement bénéfique.
            </h3>
            <p className="mt-3 max-w-5xl text-sm leading-6 text-slate-200 md:text-base">
              Le Directeur TI installe une relation structurée : gouvernance, maturité, besoins, solutions,
              livrables et trajectoire 5 ans.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <Metric value="2 à 3" label="Rencontres" detail="Entrée rapide dans la démarche client." />
            <Metric value="6 sem." label="Fenêtre" detail="Rencontres de 2h max." />
            <Metric value="5" label="Livrables" detail="Tableau, ODS, besoins, plan annuel, plan 5 ans." />
            <Metric value="1" label="Directeur TI" detail="Un point de contact principal, impartial et stratégique." />
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {directorValue.map((item) => (
              <div key={item.title} className="rounded-3xl border border-white/10 bg-white/[0.055] p-4">
                <p className="mb-1 text-base font-black text-white">{item.title}</p>
                <p className="text-xs leading-5 text-slate-300">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (activeTab === 'rencontre1') {
      return (
        <div className="space-y-7">
          <GlassCard title="Rencontre client #1" icon={Users} tone="cyan">
            <BulletList items={meetingOneItems} />
          </GlassCard>

          <GlassCard title="Le fil narratif à installer" icon={ArrowRight} tone="emerald">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {partnershipSteps.map((step, index) => (
                <div key={step.title} className="rounded-2xl border border-white/10 bg-slate-950/40 p-4">
                  <p className="mb-2 text-xs font-black uppercase tracking-[0.2em] text-cyan-200">Étape {index + 1}</p>
                  <p className="font-black text-white">{step.title}</p>
                  <p className="mt-2 text-xs leading-5 text-slate-400">{step.body}</p>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>
      );
    }

    if (activeTab === 'cycle') {
      return (
        <div className="space-y-7">
          <GlassCard title="Approche de la démarche client" icon={CalendarClock} tone="violet">
            <BulletList items={preCycleFacts} />
          </GlassCard>

          <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
            <GlassCard title="Livrables produits" icon={FileText} tone="cyan">
              <div className="grid gap-3 md:grid-cols-2">
                {deliverables.map((item) => (
                  <div key={item} className="rounded-2xl border border-white/10 bg-slate-950/40 p-4 font-bold text-white">
                    {item}
                  </div>
                ))}
              </div>
            </GlassCard>

            <GlassCard title="Transformation numérique et IA" icon={Brain} tone="amber">
              <BulletList items={aiTransformation} />
            </GlassCard>
          </div>
        </div>
      );
    }

    if (activeTab === 'portail') {
      return (
        <div className="space-y-7">
          <GlassCard title="PRIsME-Client" icon={Monitor} tone="cyan">
            <p className="text-lg font-semibold text-white">
              Plateforme collaborative du comité TI : photo de maturité,
              indicateurs, gouvernance, réunions, documents, projets, actifs et solutions.
            </p>
          </GlassCard>

          <div className="grid gap-4 md:grid-cols-2">
            {portalSections.map((section) => (
              <GlassCard key={section.title} title={section.title} icon={Layers3} tone="slate">
                <BulletList items={section.items} />
              </GlassCard>
            ))}
          </div>
        </div>
      );
    }

    if (activeTab === 'questions') {
      return (
        <div className="space-y-7">
          <GlassCard title="Questions de décision" icon={Target} tone="violet">
            <div className="grid gap-3 md:grid-cols-2">
              {questions.map((question, index) => (
                <div key={question} className="rounded-2xl border border-white/10 bg-slate-950/40 p-4">
                  <p className="mb-2 text-xs font-black uppercase tracking-[0.2em] text-violet-200">Question {index + 1}</p>
                  <p className="font-bold leading-6 text-white">{question}</p>
                </div>
              ))}
            </div>
          </GlassCard>

          <GlassCard title="Devoirs après la rencontre #1" icon={ClipboardList} tone="emerald">
            <BulletList items={homework} />
          </GlassCard>
        </div>
      );
    }

    return (
      <div className="space-y-7">
        <GlassCard title="Séquence des rencontres client #2 à #5" icon={CalendarClock} tone="cyan">
          <div className="space-y-4">
            {meetings.map((meeting) => (
              <div key={meeting.number} className="rounded-2xl border border-white/10 bg-slate-950/40 p-5">
                <div className="mb-3 flex flex-wrap items-center gap-3">
                  <span className="rounded-full border border-cyan-300/30 bg-cyan-400/10 px-3 py-1 text-xs font-black text-cyan-100">
                    Rencontre {meeting.number}
                  </span>
                  <p className="text-lg font-black text-white">{meeting.title}</p>
                </div>
                <BulletList items={meeting.items} />
                <p className="mt-4 rounded-2xl border border-amber-300/20 bg-amber-500/10 p-3 text-sm font-semibold text-amber-100">
                  Devoir : {meeting.homework}
                </p>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>
    );
  }, [activeTab]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-md">
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.22 }}
            className="flex max-h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-[2rem] border border-white/10 bg-[#05070d] shadow-2xl"
          >
            <header className="relative overflow-hidden border-b border-white/10 px-6 py-5 md:px-8">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(34,211,238,0.18),transparent_34%),radial-gradient(circle_at_85%_20%,rgba(16,185,129,0.16),transparent_30%)]" />
              <button
                type="button"
                onClick={onClose}
                className="absolute right-4 top-4 z-10 rounded-full border border-white/10 bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="relative max-w-5xl">
                <p className="mb-3 text-xs font-bold uppercase tracking-[0.32em] text-cyan-200">Projet PRIsME</p>
                <h2 className="text-3xl font-black leading-tight text-white md:text-5xl">Présentation Directeur TI</h2>
                <p className="mt-3 max-w-4xl text-sm leading-6 text-slate-200 md:text-base">
                  Guide premium des rencontres client PRIsME pour les Directeurs TI : gouvernance, cadre, besoins,
                  livrables, portail et séquence d’approbation.
                </p>
              </div>
            </header>

            <div className="flex gap-2 overflow-x-auto border-b border-white/10 bg-slate-950/80 px-4 py-3 md:px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold transition-colors ${
                      active
                        ? 'border-cyan-300/40 bg-cyan-400/15 text-white'
                        : 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <main className="flex-1 overflow-y-auto px-5 py-6 md:px-8">{tabContent}</main>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
